import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { capabilities } from '@/capabilities.ts';
import { enqueueSendToNative } from '@/bridge/embeddedBridge.ts';
import styles from './SceneSelector.module.css';

export function SceneSelector() {
  const { t } = useTranslation('topHeader');
  const project = useProjectStore(s => s.project);
  const activeSceneId = useProjectStore(s => s.activeSceneId);
  const setActiveScene = useProjectStore(s => s.setActiveScene);
  const readySceneIds = useProjectStore(s => s.readySceneIds);
  const allScenesReady = useProjectStore(s => s.allScenesReady);
  const getActiveScenes = useProjectStore(s => s.getActiveScenes);
  const allApprovals = useCommentStore(s => s.approvals);

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  if (!project || project.scenes.length <= 1) return null;

  // Determine if all displayed shots are approved
  const allApproved = useMemo(() => {
    const scenes = getActiveScenes();
    const shots = scenes.flatMap(s => s.shots);
    if (shots.length === 0) return false;
    return shots.every(shot => {
      const shotApprovals = allApprovals.filter(a => a.shotId === shot.id);
      if (shotApprovals.length === 0) return false;
      const latest = shotApprovals.reduce((l, a) => a.revision > l.revision ? a : l);
      return latest.status === 'approved';
    });
  }, [getActiveScenes, allApprovals]);

  const pillColor = allApproved ? '#4caf50' : '#888';

  // Current label
  const label = activeSceneId != null
    ? (() => {
        const scene = project.scenes.find(s => s.id === activeSceneId);
        // filter(Boolean)-joined so an empty scene name (no location set) doesn't leave a stray
        // trailing "1. " — mirrors the dropdown list below (headingText).
        return scene ? [scene.number, scene.name].filter(Boolean).join('. ') : t('scene');
      })()
    : t('allScenes');

  const handleSelect = useCallback((sceneId: number | null) => {
    if (sceneId === null && !allScenesReady) return;
    if (sceneId !== null && !readySceneIds.has(sceneId)) return;

    // In embedded mode, ask Unity to tear down / regenerate / reload:
    if (capabilities.bridge) {
      enqueueSendToNative('requestSceneChange', { sceneId: sceneId ?? -1 });
      setOpen(false);
      return;
    }

    setActiveScene(sceneId);
    setOpen(false);
  }, [allScenesReady, readySceneIds, setActiveScene]);

  // Close on outside click (check both pill wrapper and portalled popover)
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Compute popover position from pill bounding rect (portal escapes stacking contexts)
  const popoverStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!open || !pillRef.current) return undefined;
    const rect = pillRef.current.getBoundingClientRect();
    return { top: rect.bottom + 6, left: rect.left };
  }, [open]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <span
        ref={pillRef}
        className={styles.pill}
        style={{ background: pillColor }}
        onClick={() => setOpen(prev => !prev)}
        title={t('selectScene')}
      >
        <span className={styles.pillLabel}>{label}</span>
        <svg className={styles.pillArrow} width="10" height="6" viewBox="0 0 10 6">
          <path d="M0 0l5 6 5-6z" fill="currentColor" />
        </svg>
      </span>
      {open && createPortal(
        <div ref={popoverRef} className={styles.popover} style={popoverStyle}>
          <button
            className={`${styles.option} ${activeSceneId === null ? styles.optionActive : ''}`}
            disabled={!allScenesReady}
            onClick={() => handleSelect(null)}
          >
            {allScenesReady ? t('allScenes') : t('allScenesLoading')}
          </button>
          {project.scenes.filter(scene => !scene.isOmitted).map(scene => {
            const shotCount = scene.shots.length;
            const isReady = readySceneIds.has(scene.id);
            // filter(Boolean)-joined so an empty location name (no location set) doesn't leave a
            // stray double space; mirrors composeSceneCaptionRest (sceneCaption.ts). Preserves this
            // site's existing no-period-after-locationType formatting (pre-existing, not in scope).
            const headingText = [
              [scene.locationType, scene.name].filter(Boolean).join(' '),
              scene.timeOfDay,
            ].filter(Boolean).join(' — ');
            return (
              <button
                key={scene.id}
                className={`${styles.option} ${activeSceneId === scene.id ? styles.optionActive : ''}`}
                disabled={!isReady}
                onClick={() => handleSelect(scene.id)}
              >
                {scene.number}. {headingText} ({shotCount} {shotCount !== 1 ? t('shotPlural') : t('shotSingular')})
                {isReady ? '' : ` \u2022 ${t('sceneLoading')}`}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
